import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { authStateChanged } from '../auth'
import { RecentClass, RecentState } from './types'
import { ExistingChat } from '~types'

const initialState: RecentState = {
  userId: '',
  chats: [],
  classes: [],
}

const historyPath = (folderId: string, chatId: string, userId?: string) => {
  return userId
    ? `folders/${folderId}/chats/${chatId}?userId=${userId}`
    : `folders/${folderId}/chats/${chatId}`
}

export const addRecentChat = createAction(
  'addRecentChat',
  (chat: ExistingChat, folderId: string, chatId: string, userId?: string) => {
    return {
      payload: {
        path: historyPath(folderId, chatId, userId),
        name: chat.name,
        thumbnail: Object.values(chat.sections)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((section) => section.image.src)
          .find((src) => src.length !== 0),
      },
    }
  },
)

export const removeRecentChat = createAction(
  'removeRecentChat',
  (folderId: string, chatId: string, userId?: string) => {
    return {
      payload: historyPath(folderId, chatId, userId),
    }
  },
)

const reducer = createSlice({
  name: 'recent',
  initialState,
  reducers: {
    addRecentClass(state, action: PayloadAction<RecentClass>) {
      const { id, name } = action.payload

      if (id === undefined || name === undefined) {
        return
      }

      const shift = state.classes.filter((item) => item.id !== id)

      state.classes = [action.payload, ...shift].slice(0, 4)
    },
    removeRecentClass(state, action: PayloadAction<string>) {
      const id = action.payload
      state.classes = state.classes.filter((item) => item.id !== id)
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addRecentChat, (state, action) => {
        const { path, name } = action.payload
        if (path === undefined || name === undefined) {
          return
        }
        const shift = state.chats.filter((item) => item.path !== path)
        state.chats = [action.payload, ...shift].slice(0, 4)
      })
      .addCase(removeRecentChat, (state, action) => {
        const path = action.payload
        state.chats = state.chats.filter((item) => item.path !== path)
      })
      .addCase(authStateChanged, (state, action) => {
        const userId = action.payload?.claims.sub
        if (userId !== undefined && state.userId !== userId) {
          return {
            ...initialState,
            userId: userId,
          }
        }

        return state
      })
  },
})

export const { addRecentClass, removeRecentClass } = reducer.actions

export const recentReducer = reducer.reducer
